import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    getKpiGetMarketProgressThunk,
    getPlanGetByMarketIdThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";

import BIcon from "@/assets/icons/kpi-b.svg";
import LIcon from "@/assets/icons/kpi-l.svg";
import MIcon from "@/assets/icons/kpi-m.svg";
import FlexContainer from "@/components/FlexContainer";
import Header from "@/components/Header";
import Meta from "@/components/Meta";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { UniversalTabs } from "@/components/UniversalTabs";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";

import {
    StyledAPContainer,
    StyledAPPlanContainer,
    StyledAPPlanItemContainer,
    StyledAPPlanValue,
} from "../../Styled";

interface IPlanFinGroupRow {
    finGroup: string;
    b: {
        value: string | number;
        isCompleted: boolean;
    };
    l: {
        value: string | number;
        isCompleted: boolean;
    };
    m: {
        value: string | number;
        isCompleted: boolean;
    };
}

const PlanFinGroupRow: FC<IPlanFinGroupRow> = ({ finGroup, b, l, m }) => (
    <StyledAPPlanItemContainer>
        <SecondaryText fontSize={14} fontWeight="400">
            {finGroup}
        </SecondaryText>

        <FlexContainer
            width="100%"
            alignItems="center"
            justifyContent="space-between"
        >
            {[
                { icon: BIcon, ...b },
                { icon: LIcon, ...l },
                { icon: MIcon, ...m },
            ].map(({ icon, value, isCompleted }, index) => (
                <FlexContainer key={index} alignItems="center">
                    <StyledAPPlanValue
                        fontSize={12}
                        fontWeight="400"
                        $isCompleted={isCompleted}
                    >
                        {value}
                    </StyledAPPlanValue>
                    <img
                        src={icon}
                        alt={`${["b", "l", "m"][index]}-${finGroup}-icon`}
                    />
                </FlexContainer>
            ))}
        </FlexContainer>
    </StyledAPPlanItemContainer>
);

const InfoTabPlan = () => {
    const { marketId } = useParams<{ marketId: string }>();

    const { plans, products, offlineMode, facts } = useAppSelector(
        (state) => state.offline,
    );
    const dispatch = useAppDispatch();

    const currentMarketPlan = Object.entries(plans[marketId] || {});
    const currentRoutePlan = Object.entries(plans["unassignedPlan"] || {});
    const currentMarketFact = facts[marketId] || {};

    const hasMarketPlan = currentMarketPlan.length > 0;
    const hasRoutePlan = currentRoutePlan.length > 0;

    const [activeTab, setActiveTab] = useState<string>(
        hasMarketPlan ? "tt" : "route",
    );

    useEffect(() => {
        if (!hasMarketPlan && hasRoutePlan && activeTab !== "route") {
            setActiveTab("route");
        } else if (hasMarketPlan && !hasRoutePlan && activeTab !== "tt") {
            setActiveTab("tt");
        }
    }, [hasMarketPlan, hasRoutePlan, activeTab]);

    const currentMarketFinGroupsFact = useMemo(() => {
        const result: Record<string, { b: number; l: number; m: number }> = {};

        Object.entries(currentMarketFact).forEach(([barcode, fact]) => {
            const finGroup = products[barcode]?.params?.fin_group_detail
                ?.value as string;
            if (!finGroup) return;

            result[finGroup] ??= { b: 0, l: 0, m: 0 };

            if (fact?.bottle > 0) result[finGroup].b += fact.bottle;
            if (fact?.capacity > 0) result[finGroup].l += fact.capacity;
            if (fact?.money > 0) result[finGroup].m += fact.money;
        });

        return result;
    }, [currentMarketFact, products]);

    const renderValue = (
        planValue: number,
        factValue?: number,
    ): { value: string | number; isCompleted: boolean } =>
        factValue
            ? {
                  value: `${factValue} / ${planValue}`,
                  isCompleted: factValue >= planValue,
              }
            : {
                  value: planValue,
                  isCompleted: false,
              };

    const renderPlanContent = (
        planData: [string, { type: { b: number; l: number; m: number } }][],
        emptyMessage: string,
    ) => (
        <StyledAPPlanContainer>
            {planData.length === 0 ? (
                <PrimaryText
                    fontSize={14}
                    fontWeight="400"
                    style={{ textAlign: "center" }}
                >
                    {emptyMessage}
                </PrimaryText>
            ) : (
                planData.map(([finGroup, finGroupDetails]) => {
                    const fact = currentMarketFinGroupsFact[finGroup];

                    return (
                        <PlanFinGroupRow
                            key={finGroup}
                            finGroup={finGroup}
                            b={renderValue(finGroupDetails.type.b, fact?.b)}
                            l={renderValue(finGroupDetails.type.l, fact?.l)}
                            m={renderValue(finGroupDetails.type.m, fact?.m)}
                        />
                    );
                })
            )}
        </StyledAPPlanContainer>
    );

    const availableTabs = useMemo(() => {
        const tabs = [];
        if (hasMarketPlan) {
            tabs.push({ label: "На ТТ", value: "tt" });
        }
        if (hasRoutePlan) {
            tabs.push({ label: "Додатковий", value: "route" });
        }
        return tabs;
    }, [hasMarketPlan, hasRoutePlan]);

    useEffect(() => {
        if (!offlineMode) {
            dispatch(
                getPlanGetByMarketIdThunk({
                    marketId,
                    targetDate: dayjs().format(backendDateFormat.date),
                }),
            );
            dispatch(
                getKpiGetMarketProgressThunk({
                    marketId,
                    targetDate: dayjs().format(backendDateFormat.date),
                }),
            );
        }
    }, [dispatch, marketId, offlineMode]);

    return (
        <>
            <Meta title="TT Info MML" />
            <Header title="План продажів" back titleFontSize={16} />

            <StyledAPContainer>
                {!hasMarketPlan && !hasRoutePlan && (
                    <PrimaryText
                        fontSize={14}
                        fontWeight="400"
                        style={{ textAlign: "center" }}
                    >
                        План продажів не встановлено
                    </PrimaryText>
                )}

                {(hasMarketPlan || hasRoutePlan) && (
                    <>
                        <UniversalTabs
                            tabs={availableTabs}
                            value={activeTab}
                            onChange={setActiveTab}
                        />

                        {activeTab === "tt" &&
                            hasMarketPlan &&
                            renderPlanContent(
                                currentMarketPlan,
                                "План продажів на даний маркет не встановлено",
                            )}
                        {activeTab === "route" &&
                            hasRoutePlan &&
                            renderPlanContent(
                                currentRoutePlan,
                                "План продажів на маршрут не встановлено",
                            )}
                    </>
                )}
            </StyledAPContainer>
        </>
    );
};

export default InfoTabPlan;
